import { buildCycleOrderView } from './cycle-order-view';
import { buildCycleActions } from './cycle-action-view';

type Labeler = (value: string) => string;

function number(value: any) {
    const parsed = Number(value || 0);
    return Number.isFinite(parsed) ? parsed : 0;
}

function list(value: any) {
    return Array.isArray(value) ? value : [];
}

function plan(values: any[], prefix: string) {
    return list(values).length
        ? values.map(value => `${prefix}${number(value)}%`).join(' · ')
        : '-';
}


function reviewNotice(cycle: any) {
    const reasons = new Set(list(cycle?.review_reasons).map(String));
    if (reasons.has('HOLDING_D20')) {
        return '보유 20거래일 경과 · 새 매수 없이 직접 확인이 필요합니다.';
    }
    if (reasons.has('STALE_PREPARED_INTENT')) {
        return '오래된 미전송 주문이 정리되었습니다. 주문 상태를 직접 확인해주세요.';
    }
    if (reasons.has('HOLDING_D10_ATTENTION')) {
        return '보유 10거래일 경과 · 자금이 오래 묶이지 않는지 확인해주세요.';
    }
    return cycle?.status === 'REVIEW_REQUIRED'
        ? '자동 판단을 멈추고 주문·보유 상태를 직접 확인해야 합니다.'
        : '';
}

function progressLabel(name: string, completed: number, total: number) {
    return total > 0 ? `${name} ${Math.min(completed, total)}/${total}` : `${name} 없음`;
}

function highestStage(levels: any[]) {
    return list(levels).reduce((highest, level, index) => (
        Math.max(highest, number(level?.stage) || index + 1)
    ), 0);
}

export function buildCycleView(
    cycle: any,
    order: any,
    strategy: any,
    roleLabel: Labeler = () => '주문',
    statusLabel: Labeler = () => '확인 필요'
) {
    const parameters = strategy?.parameters || {};
    const cycleStatus = String(cycle?.status || '').toUpperCase();
    const entered = number(cycle?.p1) > 0;
    const positionKnown = cycle?.position_quantity !== null
        && cycle?.position_quantity !== undefined;
    const position = Math.max(0, number(cycle?.position_quantity));
    const addLevels = list(cycle?.add_levels);
    const frozenTargets = list(cycle?.target_levels);
    const continuousAdd = !!cycle?.continuous_add;
    const targetEstimated = !continuousAdd && frozenTargets.length === 0;
    const targetLevels = continuousAdd ? (cycle?.next_sell ? [cycle.next_sell] : [])
        : targetEstimated ? list(cycle?.target_preview_levels) : frozenTargets;
    const buyQuantities = list(cycle?.buy_allocation?.quantities);
    const configuredAdds = cycle?.buy_allocation
        ? Math.max(0, buyQuantities.length - 1) : list(parameters.add_levels_pct).length;
    const configuredTargets = list(parameters.exit_targets_net_pct).length;
    const rawAddCount = Math.max(
        0, number(cycle?.add_count ?? number(cycle?.buy_count) - 1)
    );
    const plannedSells = number(cycle?.sell_stage_count);
    const rawSellCount = Math.min(
        Math.max(0, number(cycle?.ladder_stage)), plannedSells || Number.MAX_SAFE_INTEGER
    );
    const maxAdd = Math.max(
        highestStage(addLevels), configuredAdds, rawAddCount
    );
    const maxSell = entered
        ? Math.max(
            plannedSells || highestStage(targetLevels) || configuredTargets,
            rawSellCount
        )
        : configuredTargets;
    const addCount = Math.min(
        rawAddCount,
        maxAdd || Number.MAX_SAFE_INTEGER
    );
    const sellCount = Math.min(
        rawSellCount,
        maxSell || Number.MAX_SAFE_INTEGER
    );
    const accumulationOpen = cycle?.accumulation_open !== false;
    const complete = cycleStatus === 'COMPLETE';
    const actionsBlocked = complete || cycleStatus === 'REVIEW_REQUIRED';
    const nextAdd = actionsBlocked
        ? null
        : addLevels.find((level: any) => ['NEXT', 'WAITING_TIME'].includes(level.status)) || null;
    const nextTarget = actionsBlocked
        ? null
        : targetLevels.find((level: any) => level.status === 'NEXT') || null;
    const actions = buildCycleActions(cycle, nextAdd, nextTarget);
    const {
        rebuyCount,
        rebuyLimit,
        rebuyOpenSlots,
        rebuyTouchedSlots,
        recycleQuantity,
    } = actions;
    const auxiliaryActions = actionsBlocked ? [] : actions.auxiliaryActions;
    const hasRebuyAction = !actionsBlocked && actions.hasRebuyAction;
    const hasRecycleAction = !actionsBlocked && actions.hasRecycleAction;
    const hasBuyCondition = !actionsBlocked && actions.hasBuyCondition;
    const hasSellCondition = !actionsBlocked && actions.hasSellCondition;
    const currentOrder = buildCycleOrderView(order, roleLabel, statusLabel);
    const currentOrderKind = String(currentOrder?.kind || '');
    const entryOrder = currentOrderKind === 'entry';
    const buyOrder = ['add', 'rebuy', 'buy'].includes(currentOrderKind);
    const sellOrder = ['sell', 'recycle-sell'].includes(currentOrderKind);

    let phaseLabel = '첫 매수 대기';
    let phaseDetail = number(parameters.entry_high_drop_pct) > 0
        ? '전일 종가·당일 고가 중 먼저 충족하는 조건에서 첫 매수합니다.'
        : '전일 종가 기준으로 첫 매수 조건을 확인합니다.';
    if (currentOrder) {
        phaseLabel = `${currentOrder.label} ${currentOrder.statusLabel}`;
        phaseDetail = currentOrder.detail;
    } else if (cycleStatus === 'REVIEW_REQUIRED') {
        phaseLabel = '직접 확인 필요';
        phaseDetail = '새 주문 없이 현재 주문과 보유 상태를 확인합니다.';
    } else if (complete) {
        phaseLabel = '사이클 완료';
        phaseDetail = '보유 수량과 미체결 주문이 없는지 최종 확인합니다.';
    } else if (cycle && !entered) {
        phaseLabel = '첫 매수 체결 대기';
        phaseDetail = '첫 매수 주문 또는 진입 조건을 확인하고 있습니다.';
    } else if (nextAdd?.status === 'WAITING_TIME') {
        phaseLabel = '추가매수 시간 대기';
        phaseDetail = `${nextAdd.stage}차 기준가는 유지됩니다. 주문 가능 시간에 조건을 다시 확인합니다.`;
    } else if (hasBuyCondition && hasSellCondition) {
        phaseLabel = '매수·매도 가격 대기';
        phaseDetail = '표시된 매수·매도 조건 중 먼저 충족되는 흐름에 대응합니다.';
    } else if (nextAdd && hasRebuyAction) {
        phaseLabel = '매수 가격 대기';
        phaseDetail = '추가 매수와 재매수 조건을 함께 확인하고 있습니다.';
    } else if (nextAdd) {
        phaseLabel = '추가 매수 대기';
        phaseDetail = `${nextAdd.stage}차 추가 매수 가격을 기다리고 있습니다.`;
    } else if (hasRebuyAction) {
        phaseLabel = '재매수 조건 대기';
        phaseDetail = rebuyTouchedSlots > 0
            ? '실시간 저점 이후 반등 조건을 확인하고 있습니다.'
            : '앞서 매도한 수량의 재매수 조건을 확인하고 있습니다.';
    } else if (nextTarget && hasRecycleAction) {
        phaseLabel = '매도 가격 대기';
        phaseDetail = '보유분과 재매수분의 매도 가격을 함께 확인하고 있습니다.';
    } else if (nextTarget) {
        phaseLabel = maxSell === 1 ? '전량 매도 대기' : `${nextTarget.stage}차 매도 대기`;
        phaseDetail = '현재 보유 수량의 다음 매도 가격을 기다리고 있습니다.';
    } else if (hasRecycleAction) {
        phaseLabel = '재매수분 매도 대기';
        phaseDetail = '재매수한 수량의 회수 가격을 기다리고 있습니다.';
    } else if (cycle) {
        const closing = positionKnown && position <= 0;
        phaseLabel = closing ? '사이클 마무리 중' : '보유 구간 확인 중';
        phaseDetail = closing
            ? '체결과 잔고가 모두 정리됐는지 확인하고 있습니다.'
            : '다음 주문 가격과 잔여 수량을 다시 확인하고 있습니다.';
    }

    let roadmap: any[];
    if (!cycle) {
        roadmap = [
            {
                key: 'entry', label: '첫 매수',
                state: buyOrder || sellOrder ? 'done' : 'current'
            },
            {
                key: 'add', label: '추가 매수',
                state: buyOrder ? 'current' : 'planned'
            },
            {
                key: 'sell', label: '분할 매도',
                state: sellOrder ? 'current' : 'planned'
            },
            { key: 'complete', label: '완료', state: 'planned' }
        ];
    } else if (complete) {
        roadmap = [
            { key: 'entry', label: '첫 매수', state: 'done' },
            { key: 'add', label: progressLabel('추가', addCount, maxAdd), state: 'done' },
            { key: 'sell', label: progressLabel('매도', sellCount, maxSell), state: 'done' },
            { key: 'complete', label: '완료', state: 'done' }
        ];
    } else {
        const addDone = entered && !buyOrder && nextAdd?.status !== 'WAITING_TIME' && (
            maxAdd === 0 || addCount >= maxAdd || !accumulationOpen
        );
        const sellDone = maxSell > 0 && sellCount >= maxSell;
        roadmap = [
            {
                key: 'entry', label: '첫 매수',
                state: entryOrder || (!entered && !buyOrder && !sellOrder)
                    ? 'current' : 'done'
            },
            {
                key: 'add',
                label: continuousAdd ? `추가 ${addCount}회` : progressLabel('추가', addCount, maxAdd),
                state: buyOrder ? 'current' : !entered ? 'planned'
                    : addDone ? 'done' : nextAdd?.status === 'WAITING_TIME' ? 'planned' : nextAdd ? 'current' : 'planned'
            },
            {
                key: 'sell',
                label: progressLabel('매도', sellCount, maxSell),
                state: sellOrder ? 'current' : !entered ? 'planned'
                    : sellDone ? 'done' : nextTarget ? 'current' : 'planned'
            },
            {
                key: 'complete',
                label: '완료',
                state: entered && positionKnown && position <= 0 && sellDone
                    ? 'current' : 'planned'
            }
        ];
    }

    let waitMessage = '';
    if (
        cycle && !currentOrder && !nextAdd && !nextTarget
        && auxiliaryActions.length === 0 && !complete
    ) {
        waitMessage = cycleStatus === 'REVIEW_REQUIRED'
            ? '직접 확인이 끝날 때까지 새 주문을 만들지 않습니다.'
            : positionKnown && position <= 0
                ? '체결과 잔고를 확인한 뒤 사이클을 마무리합니다.'
                : !accumulationOpen
                    ? '추가 매수는 종료됐습니다. 남은 수량의 매도 조건을 확인합니다.'
                    : '다음 주문 가격을 다시 계산하고 있습니다.';
    }

    return {
        statusLabel: cycle
            ? statusLabel(cycle.status)
            : currentOrder ? currentOrder.statusLabel : '대기',
        statusTone: complete ? 'complete'
            : cycleStatus === 'REVIEW_REQUIRED' ? 'warning'
                : cycle ? 'active' : currentOrder ? 'warning' : 'waiting',
        phaseLabel,
        phaseDetail,
        roadmap,
        order: currentOrder,
        nextAdd,
        nextTarget,
        targetEstimated,
        auxiliaryActions,
        hasNext: !!(nextAdd || nextTarget || auxiliaryActions.length),
        waitMessage,
        notice: reviewNotice(cycle),
        addCount,
        maxAdd,
        sellCount,
        maxSell,
        rebuyCount,
        rebuyLimit,
        showRebuyProgress: rebuyCount > 0 || rebuyOpenSlots > 0 || recycleQuantity > 0,
        rebuyProgressLabel: rebuyLimit > 0
            ? `${rebuyCount} / ${rebuyLimit}회`
            : rebuyCount > 0
                ? `${rebuyCount}회`
                : rebuyOpenSlots > 0
                    ? `${rebuyOpenSlots}개 조건`
                    : recycleQuantity > 0 ? `${recycleQuantity}주 회수 중` : '',
        addProgressLabel: continuousAdd ? `${addCount}회 · 차수 제한 없음` : maxAdd > 0 ? `${addCount} / ${maxAdd}회` : '없음',
        sellProgressLabel: maxSell > 0 ? `${sellCount} / ${maxSell}회` : '없음',
        initialRule: number(parameters.entry_drop_pct) > 0
            ? `전일 종가 -${number(parameters.entry_drop_pct)}%`
                + (number(parameters.entry_high_drop_pct) > 0
                    ? ` 또는 당일 고가 -${number(parameters.entry_high_drop_pct)}%` : '')
            : '조건 확인 중',
        addPlan: continuousAdd ? '기존 1~3차 이후 직전 기준가 -3.5%' : plan(parameters.add_levels_pct, '-'),
        buyPlan: continuousAdd ? (number(cycle?.continuous_add?.cash_pct) === 100
            ? '추가매수: 주문가능금액·운용한도 내 최대 수량 · 1주 미만 대기'
            : '추가매수: 잔여 주문가능금액 30% · 1주 미만 대기')
            : buyQuantities.map((value: any) => `${number(value)}주`).join(' → '),
        sellPlan: plan(parameters.exit_targets_net_pct, '+')
    };
}
